import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle, Info } from 'lucide-react';

export default function HealthCheck() {
  const healthData = {
    ok: true,
    timestamp: new Date().toISOString(),
    env: {
      SAFE_MODE: import.meta.env.VITE_SAFE_MODE === 'true',
      NODE_ENV: import.meta.env.MODE,
      SUPABASE_URL_SET: !!import.meta.env.VITE_SUPABASE_URL,
      SUPABASE_KEY_SET: !!import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    features: {
      onboarding: import.meta.env.VITE_SAFE_MODE !== 'true',
      search: true,
      profiles: true,
    }
  };

  const StatusBadge = ({ status }: { status: boolean }) => (
    <Badge variant={status ? "default" : "destructive"} className="ml-2">
      {status ? (
        <CheckCircle className="w-3 h-3 mr-1" />
      ) : (
        <AlertCircle className="w-3 h-3 mr-1" />
      )}
      {status ? 'OK' : 'DISABLED'}
    </Badge>
  );

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-2xl py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Info className="w-5 h-5 mr-2" />
              System Health Check
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">Overall Status</h3>
              <StatusBadge status={healthData.ok} />
              <p className="text-sm text-muted-foreground mt-1">
                Last checked: {new Date(healthData.timestamp).toLocaleString()}
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Environment</h3>
              <ul className="space-y-1">
                <li className="flex justify-between items-center">
                  Safe Mode
                  <StatusBadge status={!healthData.env.SAFE_MODE} />
                </li>
                <li className="flex justify-between items-center">
                  Environment: <Badge variant="outline">{healthData.env.NODE_ENV}</Badge>
                </li>
                <li className="flex justify-between items-center">
                  Supabase URL
                  <StatusBadge status={healthData.env.SUPABASE_URL_SET} />
                </li>
                <li className="flex justify-between items-center">
                  Supabase Key
                  <StatusBadge status={healthData.env.SUPABASE_KEY_SET} />
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Features</h3>
              <ul className="space-y-1">
                <li className="flex justify-between items-center">
                  Onboarding
                  <StatusBadge status={healthData.features.onboarding} />
                </li>
                <li className="flex justify-between items-center">
                  Search
                  <StatusBadge status={healthData.features.search} />
                </li>
                <li className="flex justify-between items-center">
                  Profiles
                  <StatusBadge status={healthData.features.profiles} />
                </li>
              </ul>
            </div>

            {import.meta.env.DEV && (
              <div className="bg-muted p-4 rounded">
                <h4 className="font-semibold mb-2">Raw Data (Dev Mode)</h4>
                <pre className="text-xs overflow-auto">
                  {JSON.stringify(healthData, null, 2)}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}